// components/workspace-timeline/useWorkspaceTimelineVolumeController.ts
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KernelHandle } from '#types/types';
import {
  AUDIO_GAIN_MAX,
  AUDIO_GAIN_SLIDER_MAX,
  AUDIO_GAIN_SLIDER_MIN,
  clamp,
  getNormalizedAudioGain,
  sliderPercentToAudioGain,
} from './presentation';
import type { ResolvedWorkspaceTimelineTrack, TimelineVolumeActions } from './types';

type VolumeAdjustDirection = 'up' | 'down';

interface VolumeHoldState {
  layerId: string;
  trackName: string;
  initialGain: number;
  latestGain: number;
  direction: VolumeAdjustDirection;
  delayTimerId: number | null;
  intervalTimerId: number | null;
  detachListeners: (() => void) | null;
}

interface VolumeSliderSessionState {
  layerId: string;
  trackName: string;
  initialGain: number;
  latestGain: number;
}

export interface UseWorkspaceTimelineVolumeControllerArgs {
  kernel: React.RefObject<KernelHandle | null>;
  resolvedTracks: ResolvedWorkspaceTimelineTrack[];
}

export interface UseWorkspaceTimelineVolumeControllerResult {
  volumeEditorTrackIdRef: React.MutableRefObject<string | null>;
  volumeOverlayTrack: ResolvedWorkspaceTimelineTrack | null;
  timelineVolumeActions: TimelineVolumeActions;
  openVolumeEditorForTrack: (track: ResolvedWorkspaceTimelineTrack) => void;
  closeVolumeEditor: () => void;
  startVolumeHold: (track: ResolvedWorkspaceTimelineTrack, direction: VolumeAdjustDirection) => void;
}

export const useWorkspaceTimelineVolumeController = ({
  kernel,
  resolvedTracks,
}: UseWorkspaceTimelineVolumeControllerArgs): UseWorkspaceTimelineVolumeControllerResult => {
  const [volumeEditorTrackId, setVolumeEditorTrackId] = useState<string | null>(null);
  const volumeEditorTrackIdRef = useRef<string | null>(null);
  const volumeHoldStateRef = useRef<VolumeHoldState | null>(null);
  const volumeSliderSessionRef = useRef<VolumeSliderSessionState | null>(null);

  useEffect(() => {
    volumeEditorTrackIdRef.current = volumeEditorTrackId;
  }, [volumeEditorTrackId]);

  const applyAudioGainToLayer = useCallback((layerId: string, gain: number) => {
    kernel.current?.updateTimelineTrackAudio(layerId, { audioGain: gain });
  }, [kernel]);

  const stepVolumeHold = useCallback((state: VolumeHoldState) => {
    const step = 0.1;
    const rawNextGain = state.direction === 'up' ? state.latestGain + step : state.latestGain - step;
    const nextGain = Math.round(clamp(rawNextGain, 0, AUDIO_GAIN_MAX) * 10) / 10;
    if (Math.abs(nextGain - state.latestGain) < 0.001) return;
    applyAudioGainToLayer(state.layerId, nextGain);
    state.latestGain = nextGain;
  }, [applyAudioGainToLayer]);

  const finalizeVolumeHold = useCallback((commit: boolean) => {
    const state = volumeHoldStateRef.current;
    if (!state) return;
    volumeHoldStateRef.current = null;

    if (state.delayTimerId !== null) {
      window.clearTimeout(state.delayTimerId);
    }
    if (state.intervalTimerId !== null) {
      window.clearInterval(state.intervalTimerId);
    }
    state.detachListeners?.();

    if (!commit) {
      applyAudioGainToLayer(state.layerId, state.initialGain);
      return;
    }
    if (Math.abs(state.latestGain - state.initialGain) < 0.001 || !kernel.current) {
      return;
    }

    kernel.current.executeAction({
      description: `${state.direction === 'up' ? 'Increase' : 'Decrease'} audio volume for "${state.trackName}"`,
      redo: () => applyAudioGainToLayer(state.layerId, state.latestGain),
      undo: () => applyAudioGainToLayer(state.layerId, state.initialGain),
    });
  }, [applyAudioGainToLayer, kernel]);

  const startVolumeHold = useCallback((
    track: ResolvedWorkspaceTimelineTrack,
    direction: VolumeAdjustDirection
  ) => {
    if (track.layerType !== 'audio') return;
    finalizeVolumeHold(true);

    const state: VolumeHoldState = {
      layerId: track.layerId,
      trackName: track.name,
      initialGain: getNormalizedAudioGain(track.audioGain),
      latestGain: getNormalizedAudioGain(track.audioGain),
      direction,
      delayTimerId: null,
      intervalTimerId: null,
      detachListeners: null,
    };
    volumeHoldStateRef.current = state;

    const handleStop = () => {
      finalizeVolumeHold(true);
    };
    window.addEventListener('mouseup', handleStop, { once: true });
    window.addEventListener('touchend', handleStop, { once: true });
    window.addEventListener('blur', handleStop, { once: true });
    state.detachListeners = () => {
      window.removeEventListener('mouseup', handleStop);
      window.removeEventListener('touchend', handleStop);
      window.removeEventListener('blur', handleStop);
    };

    stepVolumeHold(state);
    state.delayTimerId = window.setTimeout(() => {
      const current = volumeHoldStateRef.current;
      if (!current || current.layerId !== state.layerId) return;
      current.intervalTimerId = window.setInterval(() => {
        const running = volumeHoldStateRef.current;
        if (!running || running.layerId !== state.layerId) return;
        stepVolumeHold(running);
      }, 95);
    }, 240);
  }, [finalizeVolumeHold, stepVolumeHold]);

  useEffect(() => {
    return () => {
      finalizeVolumeHold(true);
    };
  }, [finalizeVolumeHold]);

  const openVolumeEditorForTrack = useCallback((track: ResolvedWorkspaceTimelineTrack) => {
    if (track.layerType !== 'audio') return;
    setVolumeEditorTrackId(track.layerId);
  }, []);

  const closeVolumeEditor = useCallback(() => {
    finalizeVolumeHold(true);
    volumeSliderSessionRef.current = null;
    setVolumeEditorTrackId(null);
  }, [finalizeVolumeHold]);

  useEffect(() => {
    if (!volumeEditorTrackId) return;
    const stillExists = resolvedTracks.some((track) => (
      track.layerId === volumeEditorTrackId &&
      track.layerType === 'audio' &&
      track.isLayerVisible
    ));
    if (stillExists) return;
    closeVolumeEditor();
  }, [closeVolumeEditor, resolvedTracks, volumeEditorTrackId]);

  const adjustTrackVolume = useCallback((
    trackId: string,
    trackName: string,
    currentAudioGain: number,
    nextPercentRaw: number
  ) => {
    const nextPercent = Math.max(
      AUDIO_GAIN_SLIDER_MIN,
      Math.min(AUDIO_GAIN_SLIDER_MAX, Math.round(nextPercentRaw))
    );
    const nextGain = sliderPercentToAudioGain(nextPercent);
    const existingSession = volumeSliderSessionRef.current;
    if (!existingSession || existingSession.layerId !== trackId) {
      volumeSliderSessionRef.current = {
        layerId: trackId,
        trackName,
        initialGain: getNormalizedAudioGain(currentAudioGain),
        latestGain: nextGain,
      };
    } else {
      existingSession.latestGain = nextGain;
    }
    applyAudioGainToLayer(trackId, nextGain);
  }, [applyAudioGainToLayer]);

  const finalizeTrackVolume = useCallback((trackId: string) => {
    if (!kernel.current) return;
    const session = volumeSliderSessionRef.current;
    if (!session || session.layerId !== trackId) return;
    volumeSliderSessionRef.current = null;
    if (Math.abs(session.latestGain - session.initialGain) < 0.001) return;
    const finalGain = session.latestGain;
    const initialGain = session.initialGain;
    kernel.current.executeAction({
      description: `Adjust audio volume for "${session.trackName}"`,
      redo: () => applyAudioGainToLayer(trackId, finalGain),
      undo: () => applyAudioGainToLayer(trackId, initialGain),
    });
  }, [applyAudioGainToLayer, kernel]);

  const volumeOverlayTrack = useMemo(() => {
    if (!volumeEditorTrackId) return null;
    return resolvedTracks.find((track) => (
      track.layerId === volumeEditorTrackId &&
      track.layerType === 'audio' &&
      track.isLayerVisible
    )) ?? null;
  }, [resolvedTracks, volumeEditorTrackId]);

  const timelineVolumeActions = useMemo<TimelineVolumeActions>(() => ({
    onChange: adjustTrackVolume,
    onFinalize: finalizeTrackVolume,
  }), [adjustTrackVolume, finalizeTrackVolume]);

  return useMemo(() => ({
    volumeEditorTrackIdRef,
    volumeOverlayTrack,
    timelineVolumeActions,
    openVolumeEditorForTrack,
    closeVolumeEditor,
    startVolumeHold,
  }), [
    closeVolumeEditor,
    openVolumeEditorForTrack,
    startVolumeHold,
    timelineVolumeActions,
    volumeOverlayTrack,
  ]);
};
